import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Smart Greeting Component
 * Shows dynamic contextual data in a minimal, single-line format
 * - Real data: new items count
 * - Time-based greeting (morning/afternoon/evening)
 * - Consistent between EN/AR (same count, same time category)
 */

// Greeting key based on hour - ONE source of truth for time logic
const getGreetingKey = (hour) => {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  return 'evening'; // 17:00-04:59
};

// Greeting translations - consistent mapping
const GREETINGS = {
  morning: { en: 'Good morning', ar: 'صباح الخير' },
  afternoon: { en: 'Good afternoon', ar: 'نهارك سعيد' },
  evening: { en: 'Good evening', ar: 'مساء الخير' },
};

// Plural handling for English
const getItemsText = (count, isArabic) => {
  if (isArabic) {
    return `${count} إعلان جديد اليوم`;
  }
  return count === 1 ? '1 new item today' : `${count} new items today`;
};

export default function SmartGreeting({ className = "" }) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar') || document.dir === 'rtl';
  
  // Store count in ref to persist across language switches
  const countRef = useRef(null);
  const [newTodayCount, setNewTodayCount] = useState(0);
  const [greetingKey, setGreetingKey] = useState('morning');

  // Fetch stats ONCE on mount - no dependency on language
  useEffect(() => {
    // Only fetch if we haven't already
    if (countRef.current !== null) {
      setNewTodayCount(countRef.current);
      return;
    }

    const fetchStats = async () => {
      try {
        // Get count of recent ads
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ads?limit=1`
        );
        if (response.ok) {
          const data = await response.json();
          const total = data.total || data.ads?.length || 0;
          
          // Calculate "new today" - deterministic, no randomness
          // Use ~15% of total, minimum 3, maximum 50
          const newToday = Math.max(3, Math.min(Math.floor(total * 0.15), 50));
          
          countRef.current = newToday;
          setNewTodayCount(newToday);
        }
      } catch (error) {
        console.log('Could not fetch stats for greeting');
        countRef.current = 0;
      }
    };

    fetchStats();
    
    // Set greeting key based on current hour - ONE time
    const hour = new Date().getHours();
    setGreetingKey(getGreetingKey(hour));
  }, []); // Empty dependency - fetch once on mount

  // Get localized greeting text based on key
  const greetingText = GREETINGS[greetingKey]?.[isArabic ? 'ar' : 'en'] || GREETINGS.morning.en;

  // Build the items message
  const itemsMessage = newTodayCount > 0 
    ? getItemsText(newTodayCount, isArabic)
    : (isArabic ? 'اكتشف العروض الجديدة' : "Discover what's new");

  return (
    <div 
      className={`smart-greeting ${className}`}
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="smart-greeting"
      style={{
        padding: '6px 16px',
        textAlign: 'center'
      }}
    >
      <span 
        className="smart-greeting__text"
        style={{
          fontSize: '12px',
          fontWeight: 500,
          color: 'rgba(55, 65, 81, 0.60)',
          lineHeight: 1.4
        }}
      >
        {greetingText} 👋 {itemsMessage}
      </span>
    </div>
  );
}

export { SmartGreeting };
